import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';

import { ApiSettings } from '../config/api-settings';
import { User, Organization, Wallet, MembershipPermissions, parseMembershipPermissions  } from '../models';
import { OrgContextService } from './org-context.service';

export interface MeResult {
  user: User;
  organization: Organization;
  organizations: Organization[];
  membership: MembershipPermissions;
  wallet: Wallet;
}

@Injectable({ providedIn: 'root' })
export class SessionService {
  private readonly _user$ = new BehaviorSubject<User | null>(null);
  private readonly _organization$ = new BehaviorSubject<Organization | null>(null);
  private readonly _organizations$ = new BehaviorSubject<Organization[]>([]);
  private readonly _membership$ = new BehaviorSubject<MembershipPermissions | null>(null);
  private readonly _wallet$ = new BehaviorSubject<Wallet | null>(null);

  readonly user$ = this._user$.asObservable();
  readonly organization$ = this._organization$.asObservable();
  readonly organizations$ = this._organizations$.asObservable();
  readonly membership$ = this._membership$.asObservable();
  readonly wallet$ = this._wallet$.asObservable();

  constructor(
    private http: HttpClient,
    private orgContext: OrgContextService,
  ) {}

  get user(): User | null { return this._user$.value; }
  get organization(): Organization | null { return this._organization$.value; }
  get membership(): MembershipPermissions | null { return this._membership$.value; }

  get authChecked(): boolean { return this._authChecked; }
  private _authChecked = false;

  loadMe(): Observable<MeResult> {
    const URL = `${ApiSettings.fullPath}me`;

    return this.http.get<any>(URL).pipe(
      map(res => ({
        user: new User(res.user),
        organization: new Organization(res.organization),
        organizations: (res.organizations ?? []).map((o: object) => new Organization(o)),
        membership: parseMembershipPermissions(res.membership),
        wallet: new Wallet(res.wallet),
      })),
      tap(me => {
        this._user$.next(me.user);
        this._organization$.next(me.organization);
        this._organizations$.next(me.organizations);
        this._membership$.next(me.membership);
        this._wallet$.next(me.wallet);
        this.orgContext.setOrgSlug(me.organization.slug);
        this.orgContext.setIsCikisiUser(me.organization.isCikisiUser);
      }),
      finalize(() => { this._authChecked = true; }),
    );
  }

  switchOrganization(slug: string): Observable<MeResult> {
    this.orgContext.setOrgSlug(slug);
    return this.loadMe();
  }

  /** Applies a partial update (e.g. a new name, or two_factor_enabled flipping)
   *  to the signed-in user without a round trip through loadMe(). */
  patchUser(partial: Partial<User>): void {
    const current = this._user$.value;
    if (!current) return;
    this._user$.next(Object.assign(current, partial));
  }

  /** Saves part of the signed-in user's preferences (PATCH /me/preferences
   *  merges at the top level: only the keys sent are replaced). The user's
   *  copy is updated first, so the UI never waits on the request; the
   *  backend's full preferences replace it when they come back. */
  updatePreferences(partial: Record<string, unknown>): Observable<Record<string, unknown>> {
    const user = this._user$.value;
    if (user) this.patchUser({ preferences: { ...(user.preferences ?? {}), ...partial } });
    const URL = `${ApiSettings.fullPath}me/preferences`;
    return this.http.patch<any>(URL, partial).pipe(
      map(res => {
        const prefs = res?.preferences ?? res?.data?.preferences ?? res;
        return prefs && typeof prefs === 'object' && !Array.isArray(prefs) ? prefs : {};
      }),
      // Only a reply that carries what was sent replaces the copy above; any
      // other shape leaves it as it is rather than wiping it.
      tap(prefs => {
        if (Object.keys(partial).every(k => k in prefs)) this.patchUser({ preferences: prefs });
      }),
    );
  }

  clear(): void {
    this._user$.next(null);
    this._organization$.next(null);
    this._organizations$.next([]);
    this._membership$.next(null);
    this._wallet$.next(null);
    this._authChecked = true;
  }

  invalidateAuthCheck(): void {
    this._authChecked = false;
  }
}
